import React, { useState } from 'react'
import i18n from 'simple-react-i18n'
import PropTypes from 'prop-types'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { isNil } from 'lodash'
import { Card, CardContent, Grid } from '@mui/material'
import RadioButtons from '../../../../components/forms/RadioButtons'
import Button from '../../../../components/forms/Button'
import Input from '../../../../components/forms/Input'
import ConfirmModal from '../../../../components/modal/ConfirmModal'
import { CardTitle } from '../../../../components/card/NewCard'
import { StyledFieldSet, StyledLegend } from '../../../../components/StyledElements'
import { getLabel } from '../../../../utils/StoreUtils'
import { getDate, getDateWithHour, getHour } from '../../../../utils/DateUtil'
import { onChangeDate, onChangeHour, setMessageModal } from '../../../../utils/FormUtils'
import { getLogin } from '../../../../utils/SettingUtils'
import ToastrAction from '../../../../toastr/actions/ToastrAction'
import PluviometryAction from '../../../actions/PluviometryAction'
import { measurePluvioType, validationSelectFiltersType } from '../../../shapes/PluvioShapes'
import ValidationSeeMoreCriterias from '../validationUtils/ValidationSeeMoreCriterias'
import { DELETE, PURGE, RESET } from '../constants/PluvioValidationConstants'

const DeletePluvioToolPanel = ({
    id,
    dataType,
    selectedCodePoint,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    isChange,
    setIsChange,
    isPreview,
    setIsPreview,
    measuresSelectedByFilters,
    createTechnicalEvent,
    displayModes,
    nextMeasure,
    selectFilters,
    changeSelectFilters,
}) => {
    const [deleteMode, setDeleteMode] = useState(RESET)
    const [modalOpen, setModalOpen] = useState(false)

    const dispatch = useDispatch()

    const {
        users,
    } = useSelector(store => ({
        users: store.UserReducer.users,
    }), shallowEqual)

    const user = users.find(u => u.login === getLogin())

    const deleteModesObj = [
        { code: RESET, name: i18n.reseting },
        { code: DELETE, name: i18n.deleting },
        user?.admin === '1' ? { code: PURGE, name: i18n.purge } : null,
    ].filter(m => !!m)

    const onValidate = () => {
        if (!displayModes.all) {
            setMessageModal(i18n.validationBruteMessage)
        } else if (!startDate || !endDate) {
            dispatch(ToastrAction.warning(i18n.datesNoSelected))
        } else {
            setIsPreview(true)
        }
    }

    const onCancel = () => {
        setIsPreview(false)
    }

    const resetSave = () => {
        setModalOpen(false)
        setIsPreview(false)
        setIsChange(!isChange)
    }

    const onSave = () => {
        if (!displayModes.all) {
            setMessageModal(i18n.validationBruteMessage)
            return
        }
        setModalOpen(true)
    }

    const onConfirm = () => {
        switch (deleteMode) {
            case PURGE:
                dispatch(PluviometryAction.purgePluvioMeasures(id, { startDate, endDate }, dataType, selectedCodePoint, selectFilters, () => {
                    resetSave()
                    createTechnicalEvent({ mode: PURGE })
                }))
                break
            case DELETE:
                dispatch(PluviometryAction.deletePluvioMeasures(id, measuresSelectedByFilters, () => {
                    createTechnicalEvent({ mode: DELETE })
                })).then(() => {
                    if (!isNil(nextMeasure)) {
                        dispatch(PluviometryAction.updateMeasures([{ ...nextMeasure, initialPoint: 1 }]))
                    }
                    resetSave()
                })
                break
            case RESET:
                dispatch(PluviometryAction.resetPluvioMeasures(id, { startDate, endDate }, dataType, selectedCodePoint, selectFilters, () => {
                    resetSave()
                    createTechnicalEvent({ mode: RESET })
                }))
                break
            default:
                break
        }
    }

    const confirmTitle = (() => {
        switch (deleteMode) {
            case PURGE: return i18n.purgeRawMeasuresConfirmation
            case DELETE: return i18n.deleteMeasuresConfirmation
            case RESET: return i18n.resetConfirmation
            default: return ''
        }
    })()

    return (
        <div>
            <Card>
                <CardTitle title={i18n.deleteMode} />
                <CardContent>
                    <Grid container sx={{ padding: '10px' }}>
                        <RadioButtons elements={ deleteModesObj } onChange={ v => setDeleteMode(v) } selected={ deleteMode } disabled={ isPreview }/>
                    </Grid>
                </CardContent>
            </Card>
            <div className='padding-top-1'/>
            <Card>
                <CardTitle title={getLabel(deleteModesObj, deleteMode)} />
                <CardContent>
                    <div className='padding-top-1'/>
                    <StyledFieldSet style={{ margin: '10px', backgroundColor: '#d9dfe4' }}>
                        <StyledLegend>{i18n.selectionCriteria}</StyledLegend>
                        <Grid container alignItems='center'>
                            <Button
                                tooltip={ i18n.selectRange }
                                className='validToolBtn margin-right-1'
                                onClick={ () => {
                                    setStartDate(undefined)
                                    setEndDate(undefined)
                                } }
                                icon='settings_ethernet'
                                disabled={ isPreview }
                            />
                            <Grid sx={{ paddingLeft: '10px', fontWeight: 'bolder' }} className='padding-left-1 bold'>{ measuresSelectedByFilters.length } { measuresSelectedByFilters.length > 1 ? i18n.selectedMeasures : i18n.selectedMeasure }</Grid>
                        </Grid>
                        <Grid container spacing={1} sx={{ paddingTop: '10px', paddingLeft: '10px', paddingRight: '10px' }}>
                            <Grid size={6}>
                                <Input
                                    title={ i18n.startDate }
                                    id='startDate'
                                    value={ getDate(startDate)}
                                    onChange={ v => {
                                        onChangeDate(v, v2 => setStartDate(getDateWithHour(v2, startDate)?.valueOf()))
                                    } }
                                    disabled={ isPreview }
                                />
                            </Grid>
                            <Grid size={6}>
                                <Input
                                    id='startDateHour'
                                    title={ i18n.startHour }
                                    value={ getHour(startDate) }
                                    onChange={ v => {
                                        onChangeHour(v, v2 => setStartDate(getDateWithHour(startDate, v2).valueOf()))
                                    } }
                                    disabled={ isPreview }
                                />
                            </Grid>
                            <Grid size={6}>
                                <Input
                                    id='endDate'
                                    value={ getDate(endDate) }
                                    title={ i18n.endDate }
                                    onChange={ v => {
                                        onChangeDate(v, v2 => setEndDate(getDateWithHour(v2, endDate).valueOf()))
                                    } }
                                    disabled={ isPreview }
                                />
                            </Grid>
                            <Grid size={6}>
                                <Input
                                    id='endDateHour'
                                    title={ i18n.endHour }
                                    value={ getHour(endDate) }
                                    onChange={ v => {
                                        onChangeHour(v, v2 => setEndDate(getDateWithHour(endDate, v2).valueOf()))
                                    } }
                                    disabled={ isPreview }
                                />
                            </Grid>
                        </Grid>
                        <ValidationSeeMoreCriterias
                            selectFilters={selectFilters}
                            changeSelectFilters={changeSelectFilters}
                            isValidateActive={isPreview}
                        />
                    </StyledFieldSet>
                    <Grid container className='padding-1' justifyContent='center'>
                        <Button
                            tooltip={ i18n.apply }
                            onClick={ onValidate }
                            icon='border_color'
                            className='btn-floating btn-large'
                            disabled={ isPreview }
                        />
                        <Button
                            tooltip={ i18n.cancel }
                            onClick={ onCancel }
                            icon='cancel'
                            className='red btn-floating btn-large margin-left-2 margin-right-2'
                            disabled={ !isPreview }
                        />
                        <Button
                            tooltip={ i18n.register }
                            onClick={ onSave }
                            icon='save'
                            disabled={ !isPreview }
                            className={ `btn-floating btn-large ${isPreview ? 'pulse' : ''}` }
                        />
                    </Grid>
                </CardContent>
            </Card>
            <ConfirmModal
                isOpen={modalOpen}
                title={confirmTitle}
                onValidate={onConfirm}
                onClose={() => setModalOpen(false)}
            />
        </div>
    )
}

DeletePluvioToolPanel.propTypes = {
    id: PropTypes.number,
    dataType: PropTypes.number,
    selectedCodePoint: PropTypes.number,
    startDate: PropTypes.number,
    setStartDate: PropTypes.func,
    endDate: PropTypes.number,
    setEndDate: PropTypes.func,
    isChange: PropTypes.bool,
    setIsChange: PropTypes.func,
    isPreview: PropTypes.bool,
    setIsPreview: PropTypes.func,
    createTechnicalEvent: PropTypes.func,
    measuresSelectedByFilters: PropTypes.arrayOf(measurePluvioType),
    displayModes: PropTypes.objectOf(PropTypes.bool),
    nextMeasure: measurePluvioType,
    selectFilters: validationSelectFiltersType,
    changeSelectFilters: PropTypes.func,
}

export default DeletePluvioToolPanel
